import { useEffect } from "react";
import { useAuth } from "@humansignal/core/providers/AuthProvider";

/**
 * Bridge component — copies the authenticated user payload into
 * ``window.APP_SETTINGS.user`` whenever it changes.
 *
 * Why: on web + desktop, Django's ``base.html`` template renders the
 * SPA shell with ``APP_SETTINGS.user`` already populated from the
 * server-side session. On the Capacitor mobile bundle there is no
 * Django serving the HTML — the bundled ``index.html`` ships with
 * ``APP_SETTINGS.user = null`` (any non-null placeholder would break
 * LSF's AppStore preprocessor, which falls back to APP_SETTINGS.user
 * when its own ``sn.user`` isn't a number and pushes that object into
 * the ``users[]`` array; an ``{id: null, …}`` placeholder would crash
 * the LSF boot with "value `null` is not assignable to type:
 * identifierNumber").
 *
 * Once ``useAuth()`` resolves, mirror the user record onto
 * ``APP_SETTINGS.user`` so LSF sees the real user. Sign-out clears it
 * back to null so a re-login as a different account never leaves the
 * previous user in place.
 */
export const AppSettingsUserSync: React.FC = () => {
  const { user } = useAuth();

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const settings = (window as any).APP_SETTINGS;
    if (!settings) return;
    settings.user = user
      ? {
          id: user.id,
          username: user.username ?? "",
          // LSF preprocessor reads the camelCase forms (it owns its
          // own ``camelizeKeys``), but normalising here keeps the
          // shape predictable for any other consumer that reads
          // ``APP_SETTINGS.user`` directly.
          firstName: user.first_name ?? "",
          lastName: user.last_name ?? "",
          email: user.email ?? "",
          initials: user.initials ?? "",
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          customHotkeys: (user as any).custom_hotkeys ?? {},
        }
      : null;
  }, [user?.id, user?.first_name, user?.last_name, user?.email, user?.username, user?.initials]);

  return null;
};
